/**
 * Registro de una vivienda: captura GPS, autodetección de sección y decisión
 * de qué hacer con la vivienda.
 *
 * Esta pantalla concentra el control de §9.4 ("GPS obligatorio: validar que
 * cada captura cae dentro del polígono de la sección INE") en el momento en que
 * sirve: antes de empezar la entrevista, no después.
 *
 * Flujo:
 *  1. Toma la coordenada con `watchPosition`, mejorando la precisión.
 *  2. Resuelve la sección contra la cartografía (servidor si hay red, copia
 *     local si no) y traduce por equivalencia, de modo que un punto en la
 *     sección 2217 valide como la 722 del marco.
 *  3. Si cae fuera, exige un motivo. No bloquea: la decisión del coordinador
 *     fue advertir y permitir continuar, para no perder trabajo legítimo por
 *     cartografía desactualizada o GPS impreciso.
 *  4. Elige: iniciar la entrevista o registrar otra disposición (rechazo,
 *     nadie en casa, no elegible, vivienda deshabitada).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { GpsDetectionResult } from '@capi/shared';

import { ClusterMap } from '../components/LazyClusterMap';
import { api } from '../api/client';
import { CACHE_KEYS, db, getCache, type DraftHousehold } from '../db/db';
import { enqueue, syncNow } from '../db/queue';
import { detectWithFallback } from '../engine/offline-geo';
import { accuracyQuality, formatAccuracy, useGps } from '../hooks/useGps';
import { blockedRemedy, canCaptureWith } from '@capi/shared';
import { useApp } from '../store';
import type { GeoJsonFeatureCollection } from '../api/client';

type Phase = 'gps' | 'blocked' | 'reason' | 'decide' | 'disposition';

export function HouseholdScreen() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    assignments,
    dispositions,
    timeSlots,
    outOfPolygonReasons,
    refreshPending,
    markSynced,
    fieldPackage,
    loadFieldData,
  } = useApp();

  const assignment = assignments.find((a) => a.id === assignmentId);
  const sequenceParam = searchParams.get('secuencia');
  const revisitUuid = searchParams.get('revisita');

  const gps = useGps();
  const [phase, setPhase] = useState<Phase>('gps');
  const [detection, setDetection] = useState<GpsDetectionResult | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [reason, setReason] = useState<string>('');
  const [reasonNote, setReasonNote] = useState('');
  const [addressNote, setAddressNote] = useState('');
  const [manzana, setManzana] = useState('');
  const [area, setArea] = useState<GeoJsonFeatureCollection | null>(null);
  const [existing, setExisting] = useState<DraftHousehold | null>(null);
  const [busy, setBusy] = useState(false);

  /* ------------------------------ Carga ------------------------------- */

  useEffect(() => {
    if (!assignment) return;
    void getCache<GeoJsonFeatureCollection>(CACHE_KEYS.geoCluster(assignment.cluster.id)).then(
      (cached) => {
        if (cached) setArea(cached.value);
      },
    );
  }, [assignment]);

  useEffect(() => {
    if (!revisitUuid) return;
    void db.households.get(revisitUuid).then((found) => {
      if (!found) return;
      setExisting(found);
      setAddressNote(found.addressNote ?? '');
      setManzana(found.manzanaCvegeo ?? '');
    });
  }, [revisitUuid]);

  // La captura arranca sola: el encuestador ya está en la puerta y una pantalla
  // con un botón "obtener ubicación" sólo añade un toque y segundos de espera.
  useEffect(() => {
    gps.capture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------------------- Autodetección -------------------------- */

  const runDetection = useCallback(async () => {
    if (!gps.reading || !assignment) return;
    setDetecting(true);
    try {
      const result = await detectWithFallback(
        {
          latitude: gps.reading.latitude,
          longitude: gps.reading.longitude,
          accuracyMeters: gps.reading.accuracyMeters,
          capturedAt: gps.reading.capturedAt,
        },
        assignment.cluster.seccion,
        assignment.cluster.id,
      );
      setDetection(result);
      /**
       * Un encuestador no captura fuera de su área asignada.
       *
       * El supervisor sí puede, justificando: es quien puede comprobar en
       * sitio si la cartografía está mal, y su criterio queda registrado.
       */
      const rol = useApp.getState().operator?.role ?? 'encuestador';
      if (result === null) {
        // Ni servidor ni geometría local: no se puede emitir veredicto.
        // Para el encuestador eso es un «no»: la alternativa sería dar por
        // buena una ubicación que nadie comprobó.
        setPhase(rol === 'encuestador' ? 'blocked' : 'reason');
      } else if (!canCaptureWith(result.status, rol)) {
        setPhase('blocked');
      } else if (result.requiresReason) {
        setPhase('reason');
      } else {
        setPhase('decide');
      }
    } finally {
      setDetecting(false);
    }
  }, [gps.reading, assignment]);

  useEffect(() => {
    if (gps.phase === 'ready' && !detection && !detecting) {
      void runDetection();
    }
  }, [gps.phase, detection, detecting, runDetection]);

  if (!assignment) {
    return (
      <div className="center-screen">
        <div>
          <p className="muted">No se encontró la asignación.</p>
          <Link to="/" className="btn btn--secondary" style={{ marginTop: 12 }}>
            Volver
          </Link>
        </div>
      </div>
    );
  }

  const sequenceNumber = existing?.sequenceNumber ?? (sequenceParam ? Number(sequenceParam) : null);

  /* ------------------------- Guardar la vivienda ---------------------- */

  const saveHousehold = async (disposition: string): Promise<DraftHousehold> => {
    const now = new Date().toISOString();
    const clientUuid = existing?.clientUuid ?? crypto.randomUUID();
    const attemptNumber = (existing?.visits.length ?? 0) + 1;

    const household: DraftHousehold = {
      clientUuid,
      assignmentId: assignment.id,
      clusterId: assignment.cluster.id,
      sequenceNumber,
      manzanaCvegeo: manzana.trim() || null,
      addressNote: addressNote.trim() || null,
      latitude: gps.reading?.latitude ?? existing?.latitude ?? null,
      longitude: gps.reading?.longitude ?? existing?.longitude ?? null,
      accuracyMeters: gps.reading?.accuracyMeters ?? existing?.accuracyMeters ?? null,
      disposition,
      eligibleAdults: existing?.eligibleAdults ?? null,
      visits: [
        ...(existing?.visits ?? []),
        {
          attemptNumber,
          timeSlot: currentTimeSlot(timeSlots),
          visitedAt: now,
          disposition,
        },
      ],
      createdAt: existing?.createdAt ?? Date.now(),
      updatedAt: Date.now(),
      queued: false,
    };

    await db.households.put(household);
    return household;
  };

  /* --------------------------- Iniciar entrevista --------------------- */

  const startInterview = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const household = await saveHousehold('entrevista_completa');
      const clientUuid = crypto.randomUUID();
      const startedAt = new Date().toISOString();

      // La aleatorización la decide el SERVIDOR: el orden de los personajes,
      // la celda del experimento con balanceo global y los recortes vigentes.
      let randomization: Awaited<ReturnType<typeof api.openInterview>> | null = null;
      try {
        randomization = await api.openInterview({
          clientUuid,
          assignmentId: assignment.id,
          isPilot: false,
          startedAt,
        });
      } catch {
        randomization = null;
      }

      if (!randomization) {
        // Sin red no se puede abrir la entrevista: la celda del experimento
        // necesita el estado acumulado de la muestra para balancearse, y
        // decidirla en el dispositivo desbalancearía el diseño de §Bloque 8.
        setBusy(false);
        window.alert(
          'Para iniciar una entrevista hace falta conexión un momento: el servidor asigna el orden ' +
            'de la tarjeta y la versión del experimento. Acércate a una zona con señal e inténtalo ' +
            'de nuevo. Lo ya capturado no se pierde.',
        );
        return;
      }

      /**
       * El cuestionario del dispositivo tiene que ser EL MISMO que el servidor
       * está registrando.
       *
       * El servidor archiva la entrevista contra el instrumento publicado hoy.
       * Si el dispositivo trae una copia anterior en caché —se publicó una
       * versión nueva mientras el encuestador ya estaba en campo— aplicaría un
       * cuestionario distinto del que queda asentado en la base: preguntas que
       * no se hicieron contarían como no respondidas y el análisis no tendría
       * forma de distinguirlas de una negativa.
       *
       * Pasó de ser hipotético al añadir el 15º personaje (W1-1.0.0 →
       * W1-1.1.0): un dispositivo sin actualizar habría preguntado por 14
       * nombres en una entrevista registrada como de 15.
       *
       * Se bloquea antes de crear el borrador, no después, para no dejar una
       * entrevista a medias. La del servidor ya quedó abierta, pero es
       * idempotente por clientUuid y este uuid no se reutiliza.
       */
      if (fieldPackage && randomization.instrumentVersion !== fieldPackage.version) {
        setBusy(false);
        window.alert(
          `El cuestionario de este dispositivo (${fieldPackage.version}) ya no es el vigente ` +
            `(${randomization.instrumentVersion}). No inicies la entrevista: ve a Ajustes y pulsa ` +
            '«Actualizar» para descargar la versión nueva. Lo ya capturado no se pierde.',
        );
        void loadFieldData();
        return;
      }

      await db.drafts.put({
        clientUuid,
        assignmentId: assignment.id,
        clusterId: assignment.cluster.id,
        expectedFrameSeccion: assignment.cluster.seccion,
        isPilot: false,
        startedAt,
        personajeOrder: randomization.personajeOrder,
        partyOrder: randomization.partyOrder,
        experimentCellId: randomization.experimentCellId,
        experimentCell: randomization.experimentCell,
        i6PersonajeIds: randomization.i6PersonajeIds,
        trimState: randomization.trimState,
        answers: {},
        cursor: null,
        gps: gps.reading
          ? {
              latitude: gps.reading.latitude,
              longitude: gps.reading.longitude,
              accuracyMeters: gps.reading.accuracyMeters,
              capturedAt: gps.reading.capturedAt,
            }
          : null,
        gpsDetection: detection,
        outOfPolygonReason: detection?.requiresReason ? reason : null,
        outOfPolygonNote: detection?.requiresReason ? reasonNote.trim() || null : null,
        coloniaDeclared: assignment.cluster.colonia,
        householdClientUuid: household.clientUuid,
        contactName: null,
        contactPhone: null,
        thirdPartyPresent: null,
        observations: null,
        updatedAt: Date.now(),
        finishedAt: null,
        terminatedAs: null,
      });

      navigate(`/entrevista/${clientUuid}`, { replace: true });
    } finally {
      setBusy(false);
    }
  };

  /* ------------------- Registrar otra disposición --------------------- */

  const recordDisposition = async (disposition: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const household = await saveHousehold(disposition);
      const definition = dispositions.find((d) => d.code === disposition);

      // §5.3.6 exige registrar la disposición de CADA vivienda contactada: sin
      // eso no se puede calcular la tasa de respuesta AAPOR RR3. Por eso las
      // viviendas sin entrevista también se encolan.
      await enqueue(
        household.clientUuid,
        'household',
        {
          clientUuid: household.clientUuid,
          clusterId: household.clusterId,
          assignmentId: household.assignmentId,
          sequenceNumber: household.sequenceNumber,
          manzanaCvegeo: household.manzanaCvegeo,
          addressNote: household.addressNote,
          latitude: household.latitude,
          longitude: household.longitude,
          accuracyMeters: household.accuracyMeters,
          disposition: household.disposition,
          eligibleAdults: household.eligibleAdults,
          visits: household.visits,
        },
        `Vivienda #${household.sequenceNumber ?? '?'} · ${definition?.label ?? disposition}`,
      );

      await db.households.update(household.clientUuid, { queued: true });
      await refreshPending();
      void syncNow().then(() => markSynced());
      navigate(`/asignacion/${assignment.id}`, { replace: true });
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------- Render ----------------------------- */

  return (
    <div className="app">
      <div className="topbar">
        <Link
          to={`/asignacion/${assignment.id}`}
          className="btn btn--ghost"
          style={{ minHeight: 'auto', padding: 4 }}
        >
          ‹ Volver
        </Link>
        <div className="topbar__title">
          {existing ? `Revisita · vivienda #${sequenceNumber}` : `Vivienda #${sequenceNumber ?? '?'}`}
          <span className="topbar__sub"> · sección {assignment.cluster.seccion}</span>
        </div>
      </div>

      <main className="app__main">
        {existing ? (
          <div className="notice notice--info">
            <span className="notice__title">
              Intento {existing.visits.length + 1} de 3
            </span>
            §5.3.4 permite hasta 3 intentos en horarios distintos antes de pasar a la siguiente
            vivienda del salto.
          </div>
        ) : null}

        {/* ----------------------------- GPS ----------------------------- */}

        <GpsPanel gps={gps} detection={detection} detecting={detecting} onRetry={() => {
          setDetection(null);
          setPhase('gps');
          gps.capture();
        }} />

        {gps.reading && area ? (
          <div style={{ marginTop: 12 }}>
            <ClusterMap
              area={area}
              position={null}
              candidate={{ latitude: gps.reading.latitude, longitude: gps.reading.longitude }}
            />
            <p className="faint" style={{ marginTop: 6 }}>
              El punto ámbar es la ubicación capturada; el contorno azul, tu área asignada.
            </p>
          </div>
        ) : null}

        {/* --------------------- Fuera del área ------------------------- */}

        {phase === 'blocked' ? (
          <div style={{ marginTop: 20 }}>
            <div className="notice notice--danger">
              <span className="notice__title">No puedes capturar aquí</span>
              {detection?.message ??
                'No se pudo verificar la ubicación contra la cartografía y no hay polígono en el dispositivo.'}
            </div>

            <p style={{ marginTop: 14 }}>
              {detection ? blockedRemedy(detection.status) : 'Avisa a tu supervisor.'}
            </p>

            {/**
             * Reintentar sólo tiene sentido cuando el problema es la lectura.
             * Ofrecerlo cuando la ubicación está claramente en otra sección
             * invita a insistir hasta que el GPS «acierte», que es justo lo
             * que el bloqueo evita.
             */}
            {detection === null ||
            detection.status === 'low-accuracy' ||
            detection.status === 'unavailable' ? (
              <button
                type="button"
                className="btn btn--primary btn--block"
                style={{ marginTop: 14 }}
                disabled={detecting}
                onClick={() => {
                  setDetection(null);
                  gps.capture();
                }}
              >
                {detecting ? 'Verificando…' : 'Volver a tomar la lectura'}
              </button>
            ) : null}

            <button
              type="button"
              className="btn btn--secondary btn--block"
              style={{ marginTop: 10 }}
              onClick={() => navigate(`/asignacion/${assignment.id}`)}
            >
              Volver a mi asignación
            </button>
          </div>
        ) : null}

        {/* ------------------- Motivo si cae fuera ---------------------- */}

        {phase === 'reason' ? (
          <div style={{ marginTop: 20 }}>
            <div className="notice notice--warning">
              <span className="notice__title">Se requiere un motivo para continuar</span>
              {detection?.message ??
                'No se pudo verificar la ubicación contra la cartografía: no hay red ni polígono en el dispositivo.'}
            </div>

            <p className="field__label">¿Por qué no coincide la ubicación?</p>
            <div className="options">
              {outOfPolygonReasons.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  className={`option${reason === option.code ? ' option--selected' : ''}`}
                  onClick={() => setReason(option.code)}
                >
                  <span className="option__marker" aria-hidden>
                    {reason === option.code ? '✓' : ''}
                  </span>
                  <span className="option__label">
                    {option.label}
                    <span className="option__hint">{option.description}</span>
                  </span>
                </button>
              ))}
            </div>

            {reason === 'otro' ? (
              <div className="field" style={{ marginTop: 14 }}>
                <label className="field__label" htmlFor="reason-note">
                  Describe el motivo
                </label>
                <textarea
                  id="reason-note"
                  className="textarea"
                  value={reasonNote}
                  onChange={(e) => setReasonNote(e.target.value)}
                />
              </div>
            ) : null}

            <p className="faint" style={{ marginTop: 12 }}>
              La entrevista queda marcada para revisión de tu supervisor, con la distancia en
              metros. No se pierde ni se invalida.
            </p>

            <button
              type="button"
              className="btn btn--primary btn--block"
              style={{ marginTop: 14 }}
              disabled={!reason || (reason === 'otro' && reasonNote.trim() === '')}
              onClick={() => setPhase('decide')}
            >
              Continuar
            </button>
          </div>
        ) : null}

        {/* ----------------------- Datos y decisión --------------------- */}

        {phase === 'decide' || phase === 'disposition' ? (
          <div style={{ marginTop: 20 }}>
            <div className="divider" />

            <div className="field">
              <label className="field__label" htmlFor="address">
                Referencia del domicilio (opcional)
              </label>
              <input
                id="address"
                className="input"
                value={addressNote}
                placeholder="Calle y número, o seña particular"
                onChange={(e) => setAddressNote(e.target.value)}
                autoComplete="off"
              />
            </div>

            {assignment.cluster.manzanas.length > 0 ? (
              <div className="field">
                <label className="field__label" htmlFor="manzana">
                  Manzana
                </label>
                <select
                  id="manzana"
                  className="select"
                  value={manzana}
                  onChange={(e) => setManzana(e.target.value)}
                >
                  <option value="">Sin especificar</option>
                  {assignment.cluster.manzanas.map((m) => (
                    <option key={m.id} value={m.cvegeo ?? ''}>
                      Manzana {m.orderIndex + 1} · {m.cvegeo ?? 'sin clave'}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {phase === 'decide' ? (
              <>
                <div className="readaloud" style={{ marginTop: 18 }}>
                  Buenos días/tardes. Mi nombre es {useApp.getState().operator?.name ?? '[nombre]'},
                  de Calimetría, una empresa de investigación de opinión. Estamos realizando un
                  estudio sobre Tecate y su comunidad. No vendemos nada. Sus respuestas son
                  anónimas y confidenciales, y se usan sólo con fines estadísticos. La entrevista
                  dura unos 20 minutos. ¿Me podría apoyar?
                </div>

                <div className="interviewer-note">
                  <span className="interviewer-note__label">Antes de tocar</span>
                  No menciones partidos ni personajes. No portes prendas, colores ni artículos de
                  ningún partido o personaje.
                </div>

                <button
                  type="button"
                  className="btn btn--primary btn--block"
                  style={{ marginTop: 16 }}
                  onClick={() => void startInterview()}
                  disabled={busy}
                >
                  {busy ? 'Preparando…' : 'Aceptó · iniciar entrevista'}
                </button>

                <button
                  type="button"
                  className="btn btn--secondary btn--block"
                  style={{ marginTop: 10 }}
                  onClick={() => setPhase('disposition')}
                  disabled={busy}
                >
                  No se realizó la entrevista
                </button>
              </>
            ) : (
              <>
                <p className="field__label" style={{ marginTop: 18 }}>
                  ¿Qué ocurrió en esta vivienda?
                </p>
                <div className="options">
                  {dispositions
                    .filter((d) => d.code !== 'entrevista_completa')
                    .map((definition) => (
                      <button
                        key={definition.code}
                        type="button"
                        className="option"
                        onClick={() => void recordDisposition(definition.code)}
                        disabled={busy}
                      >
                        <span className="option__label">
                          {definition.label}
                          <span className="option__hint">{definition.description}</span>
                        </span>
                      </button>
                    ))}
                </div>
                <button
                  type="button"
                  className="btn btn--ghost btn--block"
                  style={{ marginTop: 12 }}
                  onClick={() => setPhase('decide')}
                >
                  Atrás
                </button>
              </>
            )}
          </div>
        ) : null}
      </main>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Panel del GPS                                  */
/* -------------------------------------------------------------------------- */

function GpsPanel({
  gps,
  detection,
  detecting,
  onRetry,
}: {
  gps: ReturnType<typeof useGps>;
  detection: GpsDetectionResult | null;
  detecting: boolean;
  onRetry: () => void;
}) {
  const quality = accuracyQuality(gps.bestAccuracy);

  return (
    <div className="card">
      <div className="row row--between">
        <p className="card__title" style={{ margin: 0 }}>
          Ubicación
        </p>
        <span
          className={`badge ${
            gps.phase === 'ready' ? 'badge--ok' : gps.phase === 'acquiring' || gps.phase === 'requesting' ? 'badge--info' : 'badge--warn'
          }`}
        >
          {gps.phase === 'requesting'
            ? 'Solicitando permiso'
            : gps.phase === 'acquiring'
              ? `Buscando señal · ${gps.elapsedSeconds}s`
              : gps.phase === 'ready'
                ? formatAccuracy(gps.bestAccuracy)
                : gps.phase === 'imprecise'
                  ? `Impreciso · ${formatAccuracy(gps.bestAccuracy)}`
                  : gps.phase === 'denied'
                    ? 'Permiso denegado'
                    : 'No disponible'}
        </span>
      </div>

      {gps.phase === 'acquiring' || gps.phase === 'requesting' ? (
        <div className="row" style={{ marginTop: 12 }}>
          <div className="spinner" />
          <span className="muted">
            Mantén el teléfono quieto y al aire libre; la precisión mejora en unos segundos.
          </span>
        </div>
      ) : null}

      {gps.error ? (
        <div className="notice notice--danger" style={{ marginTop: 12, marginBottom: 0 }}>
          {gps.error}
        </div>
      ) : null}

      {gps.reading ? (
        <p className="faint mono" style={{ marginTop: 10 }}>
          {gps.reading.latitude.toFixed(6)}, {gps.reading.longitude.toFixed(6)}
        </p>
      ) : null}

      {quality === 'poor' && gps.reading ? (
        <div className="notice notice--warning" style={{ marginTop: 10, marginBottom: 0 }}>
          La precisión es baja. Sal al aire libre y vuelve a tomar la lectura antes de continuar.
        </div>
      ) : null}

      {detecting ? (
        <div className="row" style={{ marginTop: 12 }}>
          <div className="spinner" />
          <span className="muted">Verificando la sección…</span>
        </div>
      ) : null}

      {detection ? (
        <div
          className={`notice ${
            detection.valid ? 'notice--success' : 'notice--warning'
          }`}
          style={{ marginTop: 12, marginBottom: 0 }}
        >
          <span className="notice__title">
            {detection.valid ? 'Ubicación verificada' : 'Ubicación fuera del área asignada'}
          </span>
          {detection.message}
          {detection.evaluatedOffline ? (
            <p className="faint" style={{ marginTop: 6, marginBottom: 0 }}>
              Verificado en el dispositivo, sin servidor. Se confirmará al sincronizar.
            </p>
          ) : null}
        </div>
      ) : null}

      <button
        type="button"
        className="btn btn--secondary"
        style={{ marginTop: 14 }}
        onClick={onRetry}
        disabled={gps.phase === 'acquiring' || gps.phase === 'requesting' || detecting}
      >
        Volver a tomar la ubicación
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Auxiliares                                  */
/* -------------------------------------------------------------------------- */

/**
 * Franja horaria actual, para el registro del intento.
 *
 * §9.4 pide reprogramar a horario vespertino y fin de semana a partir del día 6
 * si hay subcobertura de jóvenes u hombres; registrar la franja es lo que
 * permite al coordinador saber si la reprogramación se cumplió.
 */
function currentTimeSlot(slots: readonly { code: string; label: string }[]): string {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();

  const preferred =
    day === 0 || day === 6
      ? 'fin_semana'
      : hour < 13
        ? 'manana'
        : hour < 17
          ? 'tarde'
          : 'vespertino';

  return slots.some((s) => s.code === preferred) ? preferred : (slots[0]?.code ?? 'tarde');
}
